import styled from 'styled-components';
import { createReadableClass } from '@elfsight/widget-utils';
import { useSettings } from '@wu/widget-core';
import { ColumnElements } from '../column-elements';
import { VERTICAL_PADDING } from '../constants';
import { highlightElementsMap } from '../highlight-elements';
import { useLayout } from '../layouts/layout-context';
import { useTables } from '../tables-context';
import { type ColumnBaseProps, ColumnContextProvider } from './column-provider';
import { columnsMap } from './variants';

interface ColumnProps extends ColumnBaseProps {
  columnIndex: number;
  isHeadColumn?: boolean;
}

export function Column({
  className,
  style,
  columnIndex,
  isHeadColumn = false,
  ...column
}: ColumnProps) {
  const { highlightStyleType } = useSettings();
  const HighlightComponent = highlightElementsMap[highlightStyleType];

  const { activeTable } = useTables();
  const { layout, rowsCount } = useLayout();

  const isTable = layout !== 'carousel' && layout !== 'grid';
  const isComparisonTableMobile = layout === 'comparisonTableMobile';

  const ColumnComponent = columnsMap[layout];

  const { columns = [] } = activeTable ?? {};
  const columnsLength = isComparisonTableMobile
    ? columns.length - 1
    : columns.length;

  const derivedColumnIndex = columnIndex + 1;
  const isFirstColumn = derivedColumnIndex === 1;
  const isLastColumn = derivedColumnIndex === columnsLength;

  return (
    <ColumnContextProvider
      column={column}
      index={derivedColumnIndex}
      isHeadColumn={isHeadColumn}
    >
      <ColumnComponent
        style={style}
        className={createReadableClass(
          {
            component: 'column',
            element: 'container'
          },
          [className, isHeadColumn && 'es-head-column-container']
        )}
        $index={columnIndex}
        $isFirstColumn={isFirstColumn}
        $isLastColumn={isLastColumn}
        $rowsCount={rowsCount}
      >
        <ColumnElements
          $alignRows={isTable}
          $columnIndex={derivedColumnIndex}
        />

        <HighlightComponent
          $alignRows={isTable}
          $columnIndex={derivedColumnIndex}
          $rowsCount={rowsCount}
        />

        {isTable && (
          <PaddingBlock
            $columnIndex={derivedColumnIndex}
            $rowsCount={rowsCount}
          />
        )}
      </ColumnComponent>
    </ColumnContextProvider>
  );
}

const PaddingBlock = styled.div.attrs<{
  $columnIndex: number;
  $rowsCount: number;
}>(({ $columnIndex, $rowsCount }) => ({
  className: createReadableClass({
    component: 'column',
    element: 'padding-block'
  }),
  style: {
    // Используем inline-стили чтоб не приходилось создавать динамический класс под каждую колонку
    gridArea: `${$rowsCount} / ${$columnIndex}  / ${$rowsCount + 1} / ${$columnIndex + 1}`
  }
}))`
  padding-top: ${VERTICAL_PADDING}px;
`;
